import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type { FC } from "react";
import { type IgnoredExternalShift, IgnoreShiftType } from "../../../utils/models";

type MissingNursesTableProps = {
	ignoredShifts: IgnoredExternalShift[];
};

export const MissingNursesTable: FC<MissingNursesTableProps> = ({ ignoredShifts }) => {
	const formatTime = (dateStr: string): string => DateTime.fromISO(dateStr).toFormat("h:mma");

	const calculateDuration = (startStr: string, endStr: string) => {
		const start = DateTime.fromISO(startStr);
		const end = DateTime.fromISO(endStr);
		const duration = end.diff(start, "minute").minutes;
		const hours = Math.floor(duration / 60);
		const minutes = duration % 60;

		if (!minutes) return `${hours}:00 h`;

		return `${hours}:${minutes.toString().padStart(2, "0")} h`;
	};

	const getReasonLabel = (reason: IgnoreShiftType): string => {
		switch (reason) {
			case IgnoreShiftType.NotExisting:
				return "Unknown Staff";
			case IgnoreShiftType.MultipleMatching:
				return "Ambiguous Nurse";
			case IgnoreShiftType.EmptyShift:
				return "Empty Shift";
			case IgnoreShiftType.LongShift:
				return "Shift Over 24H";
			default:
				return "Unknown";
		}
	};

	return (
		<TableContainer
			data-testid="missing-nurses-table"
			component={Paper}
			elevation={0}
			sx={{
				maxHeight: 200,
				overflow: "auto",
				"&::-webkit-scrollbar": {
					width: "8px",
				},
				"&::-webkit-scrollbar-track": {
					backgroundColor: "#f1f1f1",
				},
				"&::-webkit-scrollbar-thumb": {
					backgroundColor: "#c1c1c1",
					borderRadius: "4px",
				},
				"&::-webkit-scrollbar-thumb:hover": {
					backgroundColor: "#a1a1a1",
				},
			}}
		>
			<Table stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 600,
									color: "text.secondary",
								}}
							>
								Name
							</Typography>
						</TableCell>
						<TableCell>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 600,
									color: "text.secondary",
								}}
							>
								Shift
							</Typography>
						</TableCell>
						<TableCell>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 600,
									color: "text.secondary",
								}}
							>
								Total
							</Typography>
						</TableCell>
						<TableCell>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 600,
									color: "text.secondary",
								}}
							>
								Uploaded Data
							</Typography>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{ignoredShifts.map((shift, index) => (
						<TableRow
							data-testid={`missing-nurse-row-${index}`}
							key={`${shift.name}-${index}`}
							sx={{
								"&:hover": {
									backgroundColor: "action.hover",
								},
								borderLeft: "3px solid transparent",
							}}
						>
							<TableCell>
								<Typography
									variant="body2"
									sx={{
										color: "primary.dark",
										fontWeight: 500,
									}}
								>
									{shift.name}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography
									variant="body2"
									sx={{
										color: (theme) => alpha(theme.palette.primary.dark, 0.5),
									}}
								>
									{formatTime(shift.startTime)} - {formatTime(shift.endTime)}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography
									variant="body2"
									sx={{
										color: "primary.dark",
									}}
								>
									{calculateDuration(shift.startTime, shift.endTime)}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography
									sx={{
										color: "#d32f2f",
										fontWeight: 500,
										fontSize: "0.75rem",
									}}
								>
									{getReasonLabel(shift.reason)}
								</Typography>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
